<script setup lang="ts">
import { computed } from 'vue';
import { Head, Link } from '@inertiajs/vue3';
import SiteHeader from '@/components/SiteHeader.vue';
import SiteFooter from '@/components/SiteFooter.vue';
import HomeLayout from '@/layouts/HomeLayout.vue';
import AddressQuoteForm from '@/components/AddressQuoteForm.vue';

interface Location {
    name: string;
    slug: string;
    state: string | null;
    parent: Location | null;
}

interface KeywordTemplate {
    id: number;
    name: string;
}

interface KeywordPageData {
    id: number;
    slug: string;
    meta_title: string | null;
    meta_description: string | null;
    body_content: string | null;
    custom_content: string | null;
    internal_links: string[] | null;
    location: Location;
    template: KeywordTemplate;
}

interface ResolvedContent {
    page_title: string;
    meta_title: string;
    meta_description: string;
    h1: string;
    intro: string;
}

const props = defineProps<{
    keywordPage: KeywordPageData;
    resolvedContent: ResolvedContent;
    jsonLdSchema?: string;
}>();

defineOptions({ layout: HomeLayout });

const pageTitle = computed(
    () => props.keywordPage.meta_title ?? props.resolvedContent.meta_title,
);
const pageDescription = computed(
    () =>
        props.keywordPage.meta_description ??
        props.resolvedContent.meta_description,
);
const canonicalUrl = computed(
    () => `https://mowmanagers.com/${props.keywordPage.slug}`,
);

/** H1 comes from the template — e.g. "Lawn Mowing Service in Waco, TX" */
const fullH1 = computed(() => props.resolvedContent.h1);

/**
 * Split the H1 for styled hero — service name in white, city part in green.
 */
const heroTitle = computed(() => {
    const full = fullH1.value;
    const cityPart = `in ${props.keywordPage.location.name}`;
    const idx = full.lastIndexOf(cityPart);

    if (idx > 0) {
        return {
            main: full.substring(0, idx).trim(),
            accent: full.substring(idx).trim(),
        };
    }

    return { main: full, accent: '' };
});

const heroSubtext = computed(
    () =>
        `Affordable & reliable lawn care for homes & small businesses in ${props.keywordPage.location.name}, ${props.keywordPage.location.state ?? 'TX'}.`,
);

/** The hub market location — parent if this is a suburb, self if a market. */
const hubLocation = computed(
    () => props.keywordPage.location.parent ?? props.keywordPage.location,
);

const bodyContent = computed(
    () => props.keywordPage.body_content ?? props.keywordPage.custom_content,
);

/** Images for image+text sections. */
const sectionImages = [
    '/img/below-header-left.png',
    '/img/local-lawn-care.webp',
    '/img/below-header-middle.webp',
    '/img/below-header-right.webp',
];

interface ContentSection {
    heading: string;
    body: string;
    isCta: boolean;
    layout: 'image-text' | 'full-width';
}

/**
 * Split body content by <h2> into structured sections.
 */
const contentSections = computed((): ContentSection[] => {
    if (!bodyContent.value) {
        return [];
    }

    const parts = bodyContent.value.split(/(?=<h2>)/);
    const sections: ContentSection[] = [];

    for (const part of parts) {
        const trimmed = part.trim();

        if (!trimmed) {
            continue;
        }

        const headingMatch = trimmed.match(/<h2>(.*?)<\/h2>/s);
        const heading = headingMatch
            ? headingMatch[1].replace(/<[^>]*>/g, '')
            : '';
        const body = headingMatch
            ? trimmed.substring(trimmed.indexOf('</h2>') + 5).trim()
            : trimmed;

        const textLength = body.replace(/<[^>]*>/g, '').length;
        const layout: ContentSection['layout'] =
            textLength > 1400 ? 'full-width' : 'image-text';

        sections.push({ heading, body, isCta: false, layout });
    }

    if (sections.length > 0) {
        const last = sections[sections.length - 1];

        if (
            last.heading.toLowerCase().includes('quote') ||
            last.heading.toLowerCase().includes('get started')
        ) {
            last.isCta = true;
        }
    }

    return sections;
});

const bodySections = computed(() =>
    contentSections.value.filter((s) => !s.isCta),
);

const ctaSection = computed(
    () => contentSections.value.find((s) => s.isCta) ?? null,
);

function getImageForSection(sectionIndex: number): string {
    let imageCount = 0;

    for (let i = 0; i < sectionIndex; i++) {
        if (bodySections.value[i].layout === 'image-text') {
            imageCount++;
        }
    }

    return sectionImages[imageCount % sectionImages.length];
}

function getImageTextIndex(sectionIndex: number): number {
    let count = 0;

    for (let i = 0; i < sectionIndex; i++) {
        if (bodySections.value[i].layout === 'image-text') {
            count++;
        }
    }

    return count;
}
</script>

<template>
    <div class="bg-white">
        <Head :title="pageTitle">
            <meta
                head-key="description"
                name="description"
                :content="pageDescription"
            />
            <link rel="canonical" :href="canonicalUrl" />
            <meta
                property="og:title"
                :content="pageTitle.includes('Mow Managers') ? pageTitle : `${pageTitle} - Mow Managers`"
            />
            <meta property="og:description" :content="pageDescription" />
            <meta property="og:type" content="website" />
            <meta property="og:url" :content="canonicalUrl" />
            <meta
                property="og:image"
                content="https://mowmanagers.com/img/hero.png"
            />
            <meta
                property="og:image:alt"
                content="Mow Managers lawn care service"
            />
        </Head>

        <!-- ==================== HERO + NAV ==================== -->
        <div class="relative">
            <SiteHeader transparent />

            <section
                class="relative flex min-h-[75vh] items-center bg-cover bg-center bg-no-repeat lg:min-h-[80vh]"
                :style="{ backgroundImage: `url('/img/hero.png')` }"
            >
                <div class="absolute inset-0 bg-black/50" />

                <div
                    class="relative z-10 mx-auto w-full max-w-7xl px-4 py-32 sm:px-6 lg:px-8 lg:py-40"
                >
                    <h1
                        class="text-5xl font-bold sm:text-6xl lg:text-[90px] lg:leading-[100px] xl:text-[110px] xl:leading-[114px]"
                    >
                        <span class="text-white">{{ heroTitle.main }}</span>
                        {{ ' ' }}<br />
                        <span class="text-mm-green">{{
                            heroTitle.accent
                        }}</span>
                    </h1>

                    <p class="mt-4 max-w-xl text-[18px] font-medium text-white">
                        {{ heroSubtext }}
                    </p>

                    <AddressQuoteForm
                        variant="hero"
                        input-id-prefix="svc-hero"
                    />
                </div>
            </section>
        </div>

        <!-- ==================== BELOW-HERO IMAGES ==================== -->
        <div
            class="relative z-20 -mt-10 hidden gap-4 px-2 md:grid md:grid-cols-4 lg:-mt-16 lg:gap-6 lg:px-4"
        >
            <div class="overflow-hidden rounded-xl md:col-span-1">
                <img
                    src="/img/below-header-left.png"
                    alt="Lawn mower on grass"
                    class="aspect-[4/5] h-full w-full object-cover md:aspect-auto md:h-[340px] lg:h-[420px]"
                />
            </div>
            <div class="overflow-hidden rounded-xl md:col-span-2">
                <img
                    src="/img/below-header-middle.webp"
                    alt="Family enjoying lawn"
                    class="aspect-[4/5] h-full w-full object-cover md:aspect-auto md:h-[340px] lg:h-[420px]"
                />
            </div>
            <div class="overflow-hidden rounded-xl md:col-span-1">
                <img
                    src="/img/below-header-right.webp"
                    alt="Professional mowing"
                    class="aspect-[4/5] h-full w-full object-cover md:aspect-auto md:h-[340px] lg:h-[420px]"
                />
            </div>
        </div>

        <!-- ==================== BODY CONTENT SECTIONS ==================== -->
        <template v-for="(section, index) in bodySections" :key="index">
            <!-- ===== IMAGE + TEXT LAYOUT ===== -->
            <section
                v-if="section.layout === 'image-text'"
                :class="index % 2 === 0 ? 'bg-white' : 'bg-mm-light'"
                class="py-20 lg:py-28"
            >
                <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <article
                        class="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16"
                    >
                        <div
                            :class="
                                getImageTextIndex(index) % 2 === 1
                                    ? 'lg:order-2'
                                    : ''
                            "
                        >
                            <div class="overflow-hidden rounded-[20px]">
                                <img
                                    :src="getImageForSection(index)"
                                    :alt="`${section.heading} - Mow Managers ${keywordPage.location.name}`"
                                    class="h-[350px] w-full object-cover lg:h-[480px]"
                                    loading="lazy"
                                />
                            </div>
                        </div>

                        <div
                            :class="
                                getImageTextIndex(index) % 2 === 1
                                    ? 'lg:order-1'
                                    : ''
                            "
                        >
                            <p
                                class="text-[14px] font-extrabold tracking-[2.8px] text-mm-green uppercase"
                            >
                                {{ keywordPage.location.name }}
                                {{ keywordPage.template.name }}
                            </p>
                            <h2
                                class="mt-2 text-3xl font-bold text-mm-dark sm:text-4xl lg:text-[48px] lg:leading-[56px]"
                            >
                                {{ section.heading }}
                            </h2>
                            <div
                                class="mt-4 h-[3px] w-12 bg-mm-green"
                                aria-hidden="true"
                            />
                            <div
                                class="prose mt-6 max-w-none prose-p:text-[16px] prose-p:font-medium prose-p:leading-[26px] prose-p:text-[#6d706d] prose-li:text-[16px] prose-li:font-medium prose-li:leading-[26px] prose-li:text-[#6d706d] prose-strong:text-mm-dark prose-ol:text-[#6d706d]"
                                v-html="section.body"
                            />
                        </div>
                    </article>
                </div>
            </section>

            <!-- ===== FULL-WIDTH TEXT LAYOUT ===== -->
            <section
                v-else-if="section.layout === 'full-width'"
                :class="index % 2 === 0 ? 'bg-white' : 'bg-mm-light'"
                class="py-20 lg:py-28"
            >
                <div class="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
                    <div class="mb-8 text-center">
                        <p
                            class="text-[14px] font-extrabold tracking-[2.8px] text-mm-green uppercase"
                        >
                            {{ keywordPage.location.name }}
                            {{ keywordPage.template.name }}
                        </p>
                        <h2
                            class="mt-2 text-3xl font-bold text-mm-dark sm:text-4xl lg:text-[48px] lg:leading-[56px]"
                        >
                            {{ section.heading }}
                        </h2>
                        <div
                            class="mx-auto mt-4 h-[3px] w-12 bg-mm-green"
                            aria-hidden="true"
                        />
                    </div>
                    <div
                        class="prose prose-lg mx-auto max-w-none prose-p:text-[17px] prose-p:font-medium prose-p:leading-[30px] prose-p:text-[#6d706d] prose-li:text-[17px] prose-li:font-medium prose-li:leading-[30px] prose-li:text-[#6d706d] prose-strong:text-mm-dark"
                        v-html="section.body"
                    />
                </div>
            </section>
        </template>

        <!-- ==================== CTA SECTION ==================== -->
        <section v-if="ctaSection" class="bg-mm-dark py-20 lg:py-28">
            <div
                class="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8"
            >
                <p
                    class="text-[21px] font-extrabold tracking-[4.2px] text-mm-green uppercase"
                >
                    {{ keywordPage.location.name }}
                    {{ keywordPage.template.name }}
                </p>
                <h2
                    class="mt-3 text-3xl font-bold text-white sm:text-4xl lg:text-5xl"
                >
                    {{ ctaSection.heading }}
                </h2>
                <div
                    class="prose mx-auto mt-6 max-w-xl prose-p:text-[18px] prose-p:leading-[30px] prose-p:font-medium prose-p:text-white/80"
                    v-html="ctaSection.body"
                />
                <Link
                    href="/get-started"
                    class="mt-8 inline-block cursor-pointer rounded-md bg-mm-green px-10 py-4 text-[16px] font-bold tracking-[1.6px] text-white uppercase transition-colors hover:bg-mm-green-dark"
                >
                    Get Started
                </Link>
            </div>
        </section>

        <!-- ==================== INTERNAL LINKS ==================== -->
        <section class="bg-mm-light py-16 lg:py-20">
            <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div class="text-center">
                    <p
                        class="text-[21px] font-extrabold tracking-[4.2px] text-mm-green uppercase"
                    >
                        Explore More
                    </p>
                    <div class="mt-8 flex flex-wrap justify-center gap-4">
                        <Link
                            v-for="slug in (
                                keywordPage.internal_links ?? []
                            ).filter(
                                (s) =>
                                    s !== keywordPage.location.slug &&
                                    s !== hubLocation.slug,
                            )"
                            :key="slug"
                            :href="`/${slug}`"
                            class="rounded-md border-2 border-mm-green px-6 py-3 text-[15px] font-bold tracking-[1.6px] text-mm-green uppercase transition-colors hover:bg-mm-green hover:text-white"
                        >
                            {{ slug.replace(/-/g, ' ') }}
                        </Link>
                        <Link
                            :href="`/${hubLocation.slug}`"
                            class="rounded-md border-2 border-mm-green px-6 py-3 text-[15px] font-bold tracking-[1.6px] text-mm-green uppercase transition-colors hover:bg-mm-green hover:text-white"
                        >
                            {{ hubLocation.name }}
                        </Link>
                    </div>
                </div>
            </div>
        </section>

        <SiteFooter />
    </div>
</template>
